import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Eye, EyeOff, CheckCircle2, AlertTriangle } from 'lucide-react';
import { formatApiError } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_PASSWORD_LEN = 8;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = (params.get('token') || '').trim();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < MIN_PASSWORD_LEN) {
      setError(`Password must be at least ${MIN_PASSWORD_LEN} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords don\u2019t match.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2200);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16 flex items-center">
        <div className="max-w-md mx-auto w-full px-6">
          <div className="px-4 py-5 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded-md" data-testid="reset-password-no-token">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-enf-accent-fg mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-semibold text-enf-accent-fg mb-1">Missing reset token</div>
                <p className="text-sm text-enf-text-secondary leading-relaxed">
                  This page expects a reset token in the URL. Make sure you opened the most recent
                  reset link from your email.
                </p>
              </div>
            </div>
            <div className="mt-5 text-sm text-enf-text-muted">
              <Link to="/forgot-password" className="text-enf-role-info hover:text-enf-text-primary font-semibold">Request a new link</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16 flex items-center">
      <div className="max-w-md mx-auto w-full px-6">
        <div className="mb-6">
          <span className="enf-brand-pill inline-flex items-center">
            <img
              src="/brand/enferno-wordmark-nav@2x.png"
              srcSet="/brand/enferno-wordmark-nav.png 1x, /brand/enferno-wordmark-nav@2x.png 2x"
              alt="Enferno"
              className="h-12 w-auto"
              loading="eager"
            />
          </span>
        </div>

        <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2">New password</div>
        <h1 className="font-display text-5xl tracking-wide leading-none mb-3">Reset it.</h1>
        <p className="text-enf-text-secondary mb-8 text-sm">Pick something you&apos;ll actually remember.</p>

        {done ? (
          <div className="px-4 py-5 bg-emerald-950/40 border border-emerald-900/60 rounded-md" data-testid="reset-password-done">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-semibold text-emerald-200 mb-1">Password updated</div>
                <p className="text-sm text-enf-text-secondary leading-relaxed">
                  You&apos;re all set. Redirecting you to sign in&hellip;
                </p>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">New password</span>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'} required autoComplete="new-password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                  data-testid="reset-password-new"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-enf-text-muted hover:text-enf-text-primary p-1" aria-label={showPw ? 'Hide password' : 'Show password'}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </label>

            <label className="block">
              <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">Confirm password</span>
              <input
                type={showPw ? 'text' : 'password'} required autoComplete="new-password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-3 py-2.5 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="reset-password-confirm"
              />
            </label>

            {error && (
              <div className="px-3 py-2 bg-enf-role-danger/10 border border-enf-role-danger/40 rounded-md text-sm text-enf-role-danger" data-testid="reset-password-error">{error}</div>
            )}

            <button
              type="submit" disabled={busy}
              className="w-full py-3 bg-enf-accent hover:bg-enf-accent-hover disabled:bg-enf-surface-elevated disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
              data-testid="reset-password-submit"
            >
              {busy ? 'Updating...' : <>Update password <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
